import { useIntegration, useIntegrationCapabilities } from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useFlag } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@hostesa/ui';
import { Plug } from 'lucide-react';
import { useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { namespace } from './i18n';
import { IntegrationCard } from './integration-card';
import { IntegrationConfigForm } from './integration-config-form';

/**
 * INTEGRATIONS page — PMS configuration + capabilities + test/status/disable.
 *
 * The backend has no integration-list endpoint yet, so the page resolves the
 * active integration by id: once `useConfigureIntegration` returns, we track
 * the returned id and load its safe (no-credentials) view + capabilities. Until
 * then we show the write-only config form (and a calm EmptyState).
 *
 * Guards: feature flag `integrations_management`; RBAC `integration:read`
 * (view) and `integration:manage` (configure / test / disable). Credentials are
 * never displayed — the config form inputs are write-only.
 */
export function IntegrationsPage() {
  const { t } = useT(namespace);
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { can } = useAuth();
  const enabled = useFlag('integrations_management');

  const canRead = can('integration:read');
  const canManage = can('integration:manage');

  const [integrationId, setIntegrationId] = useState('');

  const integration = useIntegration(tenantId, canRead ? integrationId : '');
  const capabilities = useIntegrationCapabilities(tenantId, canRead ? integrationId : '');

  const header = (
    <PageHeader
      eyebrow={t('page.eyebrow')}
      title={t('page.title')}
      description={t('page.description')}
    />
  );

  if (!enabled) {
    return (
      <div data-testid="integrations-page" className="flex flex-col gap-6">
        {header}
        <EmptyState
          data-testid="integrations-disabled-state"
          icon={<Plug />}
          title={t('disabledFlag.title')}
          description={t('disabledFlag.description')}
        />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div data-testid="integrations-page" className="flex flex-col gap-6">
        {header}
        <EmptyState
          data-testid="integrations-no-access-state"
          title={t('noAccess.title')}
          description={t('noAccess.description')}
        />
      </div>
    );
  }

  return (
    <div data-testid="integrations-page" className="flex flex-col gap-6">
      {header}

      {integrationId.length === 0 ? (
        canManage ? (
          <IntegrationConfigForm
            tenantId={tenantId}
            hotelId={hotelId}
            onConfigured={(created) => setIntegrationId(created.id)}
          />
        ) : (
          <EmptyState
            data-testid="integrations-empty-state"
            icon={<Plug />}
            title={t('empty.title')}
            description={t('empty.description')}
          />
        )
      ) : integration.isLoading ? (
        <LoadingState data-testid="integration-loading" title={t('states.loading')} />
      ) : integration.isError ? (
        <ErrorState
          data-testid="integration-error"
          title={t('states.errorTitle')}
          description={t('states.errorDescription')}
        />
      ) : integration.data ? (
        <>
          <IntegrationCard
            tenantId={tenantId}
            timezone={timezone}
            integration={integration.data}
            capabilities={capabilities.data}
          />
          {canManage && (
            <IntegrationConfigForm
              tenantId={tenantId}
              hotelId={hotelId}
              onConfigured={(created) => setIntegrationId(created.id)}
            />
          )}
        </>
      ) : (
        <EmptyState
          data-testid="integrations-empty-state"
          icon={<Plug />}
          title={t('empty.title')}
          description={t('empty.description')}
        />
      )}
    </div>
  );
}
